import { RichText as HyRightText } from '@graphcms/rich-text-react-renderer';
import React, { isValidElement } from 'react';

import clsxm from '@/lib/clsxm';

import HygraphImage from '@/components/hygraph-image';
import CheckmarkIcon from '@/components/icons/checkmark-icon';
import Link from '@/components/link';
import { Typography, TypographyColor } from '@/components/typography';

export type HygraphRichTextProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
  // references?: Array<ResourceBlogBodyRichTextEmbeddedTypes>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  references?: Array<any>;
  useCheckmarkForLIs?: boolean;
  color?: TypographyColor;
};

export default function HygraphRichText(props: HygraphRichTextProps) {
  const { color, content, references, useCheckmarkForLIs } = props;
  if (!content) return <></>;

  const typographyProps = { className: 'mb-3', color: color };

  return (
    <HyRightText
      content={content}
      references={references}
      renderers={{
        h1: ({ children }) => (
          <Typography.Heading1 {...typographyProps}>
            {children}
          </Typography.Heading1>
        ),
        h2: ({ children }) => (
          <Typography.Heading2
            {...{
              ...typographyProps,
              className: clsxm(typographyProps.className, 'leading-[110%]'),
            }}
          >
            {children}
          </Typography.Heading2>
        ),
        h3: ({ children }) => (
          <Typography.Heading3
            {...typographyProps}
            overline
            className='mb-3 text-[24px]'
          >
            {children}
          </Typography.Heading3>
        ),
        h4: ({ children }) => (
          <Typography.Heading4 {...typographyProps}>
            {children}
          </Typography.Heading4>
        ),
        h5: ({ children }) => (
          <Typography.Heading5 {...typographyProps}>
            {children}
          </Typography.Heading5>
        ),
        h6: ({ children }) => (
          <Typography.Heading6 {...typographyProps}>
            {children}
          </Typography.Heading6>
        ),
        a: ({ id, children, href }) => {
          if (!children) return <></>;
          return (
            <Link id={id} href={href || '#'}>
              {children}
            </Link>
          );
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        p: ({ children, className }) => {
          if (!isValidElement(children)) return <></>;
          const mappedChildren = children.props.content?.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (child: any, index: number) => {
              if (child.italic)
                return (
                  <span key={index} className='italic'>
                    {child.text}
                  </span>
                );
              if (child.bold)
                return (
                  <span key={index} className='font-bold'>
                    {child.text}
                  </span>
                );
              if (child.underline)
                return (
                  <span key={index} className='underline'>
                    {child.text}
                  </span>
                );

              if (child.type === 'link') {
                return (
                  <Link href={child.href} key={index}>
                    {child.children[0].text}
                  </Link>
                );
              }

              if (!child.text) return null;
              return <React.Fragment key={index}>{child.text}</React.Fragment>;
            }
          );

          return (
            <Typography.Body2
              {...typographyProps}
              as='p'
              className={clsxm('mb-5 last:mb-0', className)}
            >
              {mappedChildren}
            </Typography.Body2>
          );
        },
        bold: ({ children }) => (
          <Typography.InlineBold>{children}</Typography.InlineBold>
        ),
        italic: ({ children }) => (
          <Typography.InlineItalic>{children}</Typography.InlineItalic>
        ),
        underline: ({ children }) => (
          <Typography.InlineUnderline>{children}</Typography.InlineUnderline>
        ),

        table: ({ children }) => (
          <table rules='all' className='mx-auto table-fixed'>
            {children}
          </table>
        ),
        table_head: ({ children }) => <thead>{children}</thead>,
        table_body: ({ children }) => <tbody>{children}</tbody>,
        table_row: ({ children }) => <tr className='flex'>{children}</tr>,
        table_cell: ({ children }) => (
          <Typography.Body2
            {...typographyProps}
            as='td'
            className='w-full min-w-[10rem] border-separate border-spacing-x-8 border p-3'
          >
            {children}
          </Typography.Body2>
        ),
        table_header_cell: ({ children }) => (
          <Typography.Body2
            {...typographyProps}
            as='td'
            className='w-full min-w-[10rem] border-separate border-spacing-x-8 border p-3 text-center'
          >
            {children}
          </Typography.Body2>
        ),

        blockquote: ({ children }) => (
          <Typography.Body2
            {...typographyProps}
            className='my-4 whitespace-pre-line rounded-xl bg-background-primary px-4 py-2'
          >
            {children}
          </Typography.Body2>
        ),
        code: ({ children }) => (
          <Typography.Body2
            {...typographyProps}
            className='my-4 rounded-xl bg-background-primary px-4 py-2'
          >
            {children}
          </Typography.Body2>
        ),
        code_block: ({ children }) => (
          <Typography.Body2
            {...typographyProps}
            className='my-4 whitespace-pre rounded-xl bg-background-primary px-4 py-2'
          >
            {children}
          </Typography.Body2>
        ),
        ul: ({ children }) => (
          <Typography.Body2
            {...typographyProps}
            as='ul'
            className='mb-5 ml-5 list-disc'
          >
            {children}
          </Typography.Body2>
        ),
        ol: ({ children }) => (
          <Typography.Body2
            {...typographyProps}
            as='ol'
            className='mb-5 ml-5 list-decimal'
          >
            {children}
          </Typography.Body2>
        ),
        li: ({ children }) => {
          if (useCheckmarkForLIs) {
            return (
              <Typography.Body2
                {...typographyProps}
                as='li'
                className='relative mb-2 ml-4 list-none pl-3 align-middle'
              >
                <CheckmarkIcon inline className='absolute -left-5 top-0' />
                {children}
              </Typography.Body2>
            );
          }

          return (
            <Typography.Body2
              {...typographyProps}
              as='li'
              className='mb-2 ml-4'
            >
              {children}
            </Typography.Body2>
          );
        },

        class: ({ children, className }) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (children.props.content && children.props.content.length > 0) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            children.props.content[0].className = className;
          }

          return <>{children}</>;
        },

        img: ({ src, altText, height, width }) => {
          if (!src || !height || !width) return <></>;

          return (
            <HygraphImage
              url={src}
              description={altText}
              height={height}
              width={width}
              roundedCorners
            />
          );
        },
      }}
    />
  );
}

// Used with static components to convert a string to the valid content object used by HygraphRichText
export const textToContent = (
  text: string,
  list?: string[]
): HygraphRichTextProps => ({
  content: {
    children: [
      {
        type: 'paragraph',
        children: [
          {
            text,
          },
        ],
      },
      ...(list
        ? [
            {
              type: 'class',
              className: 'checkmarks',
              children: [
                {
                  type: 'bulleted-list',
                  children: list.map((item) => ({
                    type: 'list-item',
                    children: [
                      {
                        type: 'list-item-child',
                        children: [
                          {
                            text: item,
                          },
                        ],
                      },
                    ],
                  })),
                },
              ],
            },
          ]
        : []),
    ],
  },
});
